import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus, Search, AlertTriangle, Clock, CheckCircle2,
  XCircle, Calendar as CalendarIcon, MessageCircle,
  Mail, MapPin, FileText, Pencil, Trash2,
  Building2, Trophy, Target,
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../../components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { leadsApi } from '../../lib/api';

// ---------------- Configuración estática ----------------

const ESTADOS = [
  { id: 'nuevo',       label: 'Nuevo lead',         color: 'bg-slate-100 text-slate-700 border-slate-300' },
  { id: 'llamada',     label: 'Llamada coordinada', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  { id: 'visita',      label: 'Visita coordinada',  color: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
  { id: 'propuesta',   label: 'Propuesta enviada',  color: 'bg-violet-100 text-violet-700 border-violet-300' },
  { id: 'negociacion', label: 'Negociación',        color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { id: 'legales',     label: 'Legales',            color: 'bg-orange-100 text-orange-700 border-orange-300' },
];

const TIPOLOGIAS = ['Oficina', 'Local', 'Cowork', 'Vivienda', 'Cochera', 'Baulera'];
const PROYECTOS = ['Torre Emilia', 'Foresta', 'Senderos'];
const TIPOS_OP = ['Alquiler', 'Compra'];
const USOS_BUSCADOS = ['Quiere usar', 'Quiere renta', 'Ambos'];
const ORIGENES = ['WhatsApp', 'Instagram', 'Referido', 'Web', 'Cartel / visita espontánea', 'Inmobiliaria', 'Base propia', 'Otro'];
const PRIORIDADES = [
  { id: 'alta',  label: 'Alta',  color: 'bg-rose-100 text-rose-700' },
  { id: 'media', label: 'Media', color: 'bg-amber-100 text-amber-700' },
  { id: 'baja',  label: 'Baja',  color: 'bg-slate-100 text-slate-600' },
];
const MOTIVOS_PERDIDA = [
  'Precio alto', 'No responde', 'No le sirve la ubicación', 'No le sirve el tamaño',
  'No le sirve la fecha de entrega', 'Eligió otra opción', 'No califica',
  'Consulta duplicada', 'Otro',
];

// ---------------- Utilidades ----------------

const startOfTodayMs = () => { const d = new Date(); d.setHours(0,0,0,0); return d.getTime(); };
const endOfTodayMs   = () => { const d = new Date(); d.setHours(23,59,59,999); return d.getTime(); };

const parseDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const isAbierto = (l) => (l.resultado || 'abierto') === 'abierto';

const isOverdue = (lead) => {
  if (!isAbierto(lead) || !lead.proximo_contacto) return false;
  const d = parseDate(lead.proximo_contacto);
  return !!d && d.getTime() < startOfTodayMs();
};

const isToday = (lead) => {
  if (!lead.proximo_contacto) return false;
  const d = parseDate(lead.proximo_contacto);
  if (!d) return false;
  return d.getTime() >= startOfTodayMs() && d.getTime() <= endOfTodayMs();
};

const formatProximo = (v) => {
  const d = parseDate(v);
  if (!d) return '—';
  const today = startOfTodayMs();
  const ms = d.getTime();
  const fmtTime = d.toTimeString().slice(0, 5);
  const fmtDay = d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
  if (ms >= today && ms <= today + 24*60*60*1000 - 1) return `Hoy ${fmtTime}`;
  if (ms >= today + 24*60*60*1000 && ms <= today + 48*60*60*1000 - 1) return `Mañana ${fmtTime}`;
  return `${fmtDay} ${fmtTime}`;
};

const diasEnEtapa = (lead) => {
  const ref = lead.fecha_estado || lead.created_at;
  const d = parseDate(ref);
  if (!d) return 0;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / (24 * 60 * 60 * 1000)));
};

const estadoLabel = (id) => ESTADOS.find(e => e.id === id)?.label || id;

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ---------------- Componente raíz ----------------

const ComercialLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey(k => k + 1);

  // Tab
  const [view, setView] = useState('tablero'); // 'tablero' | 'metricas'

  // Filtros
  const [search, setSearch] = useState('');
  const [fProyecto, setFProyecto] = useState('todos');
  const [fOperacion, setFOperacion] = useState('todos');
  const [fTipologia, setFTipologia] = useState('todos');

  // Dialogs / drawer
  const [showNew, setShowNew] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [pendingPerdido, setPendingPerdido] = useState(null);

  // Drag
  const [dragLeadId, setDragLeadId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await leadsApi.list();
        if (alive) setLeads(res.data || []);
      } catch (e) {
        if (alive) toast.error('No se pudieron cargar los leads');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [reloadKey]);

  // Sólo leads abiertos van al tablero. Métricas usa todos.
  const filteredTablero = useMemo(() => {
    const s = search.trim().toLowerCase();
    return leads.filter(l => {
      if (!isAbierto(l)) return false;
      if (s) {
        const blob = `${l.nombre || ''} ${l.apellido || ''} ${l.email || ''} ${l.whatsapp || ''} ${l.empresa || ''}`.toLowerCase();
        if (!blob.includes(s)) return false;
      }
      if (fProyecto !== 'todos' && l.proyecto_edificio !== fProyecto) return false;
      if (fOperacion !== 'todos' && l.tipo_operacion !== fOperacion) return false;
      if (fTipologia !== 'todos' && l.tipologia !== fTipologia) return false;
      return true;
    });
  }, [leads, search, fProyecto, fOperacion, fTipologia]);

  const grouped = useMemo(() => {
    const m = Object.fromEntries(ESTADOS.map(e => [e.id, []]));
    for (const l of filteredTablero) {
      if (m[l.estado]) m[l.estado].push(l);
      else m['nuevo'].push(l);
    }
    return m;
  }, [filteredTablero]);

  // ---------------- Acciones ----------------
  const handleMove = async (leadId, nuevoEstado) => {
    const lead = leads.find(l => l.lead_id === leadId);
    if (!lead || lead.estado === nuevoEstado) return;
    try {
      await leadsApi.changeEstado(leadId, { estado: nuevoEstado });
      reload();
    } catch (e) {
      toast.error('No se pudo cambiar el estado');
    }
  };

  const handleGanado = async (leadId) => {
    try {
      await leadsApi.changeResultado(leadId, { resultado: 'ganado' });
      reload();
    } catch (e) {
      toast.error('No se pudo marcar como ganado');
    }
  };

  const handlePerdidoRequest = (leadId) => setPendingPerdido({ leadId });
  const handleReabrir = async (leadId) => {
    try {
      await leadsApi.changeResultado(leadId, { resultado: 'abierto' });
      reload();
    } catch (e) {
      toast.error('No se pudo reabrir');
    }
  };

  // Drag and Drop
  const onDragStart = (leadId) => setDragLeadId(leadId);
  const onDragEnd   = () => { setDragLeadId(null); setDragOverCol(null); };
  const onColDragOver = (e, colId) => { e.preventDefault(); setDragOverCol(colId); };
  const onColDrop  = (colId) => {
    if (dragLeadId) handleMove(dragLeadId, colId);
    setDragLeadId(null); setDragOverCol(null);
  };

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto" data-testid="comercial-leads-page">
      {/* Línea 1: título + tabs */}
      <div className="flex items-center gap-4 mb-3 flex-wrap">
        <h1 className="text-2xl font-semibold text-slate-900">Leads</h1>
        <div className="inline-flex bg-slate-100 rounded-lg p-1">
          <button
            onClick={() => setView('tablero')}
            className={`px-3.5 py-1.5 text-sm rounded-md transition ${view === 'tablero' ? 'bg-white text-slate-900 shadow-sm font-medium' : 'text-slate-600 hover:text-slate-900'}`}
            data-testid="leads-tab-tablero"
          >Tablero</button>
          <button
            onClick={() => setView('metricas')}
            className={`px-3.5 py-1.5 text-sm rounded-md transition ${view === 'metricas' ? 'bg-white text-slate-900 shadow-sm font-medium' : 'text-slate-600 hover:text-slate-900'}`}
            data-testid="leads-tab-metricas"
          >Métricas</button>
        </div>
      </div>

      {view === 'tablero' && (
        <>
          {/* Línea 2: buscador + filtros + nuevo lead a la derecha */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-[180px] max-w-[300px]">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Buscar nombre, email o WhatsApp…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9"
                data-testid="leads-search-input"
              />
            </div>
            <FilterSelect value={fProyecto} onChange={setFProyecto} shortLabel="Proyecto"
              options={[{ value: 'todos', label: 'Todos' }, ...PROYECTOS.map(p => ({ value: p, label: p }))]}
              testid="leads-filter-proyecto" />
            <FilterSelect value={fOperacion} onChange={setFOperacion} shortLabel="Operación"
              options={[{ value: 'todos', label: 'Todas' }, ...TIPOS_OP.map(p => ({ value: p, label: p }))]}
              testid="leads-filter-operacion" />
            <FilterSelect value={fTipologia} onChange={setFTipologia} shortLabel="Tipología"
              options={[{ value: 'todos', label: 'Todas' }, ...TIPOLOGIAS.map(p => ({ value: p, label: p }))]}
              testid="leads-filter-tipologia" />

            <Button
              onClick={() => setShowNew(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white ml-auto"
              data-testid="leads-new-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Nuevo lead
            </Button>
          </div>

          {/* Kanban */}
          {loading ? (
            <div className="text-center text-slate-500 py-12">Cargando…</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5" data-testid="leads-kanban">
              {ESTADOS.map(col => (
                <KanbanColumn
                  key={col.id}
                  col={col}
                  leads={grouped[col.id] || []}
                  isDragOver={dragOverCol === col.id}
                  onDragOver={(e) => onColDragOver(e, col.id)}
                  onDragLeave={() => setDragOverCol(null)}
                  onDrop={() => onColDrop(col.id)}
                  onCardClick={(id) => setSelectedId(id)}
                  onCardDragStart={onDragStart}
                  onCardDragEnd={onDragEnd}
                />
              ))}
            </div>
          )}
        </>
      )}

      {view === 'metricas' && (
        <MetricasView leads={leads} loading={loading} />
      )}

      {selectedId && (
        <LeadDrawer
          leadId={selectedId}
          open={!!selectedId}
          onOpenChange={(v) => { if (!v) setSelectedId(null); }}
          onChanged={reload}
          onMarkGanado={handleGanado}
          onMarkPerdido={(id) => { handlePerdidoRequest(id); setSelectedId(null); }}
          onReabrir={handleReabrir}
        />
      )}

      {showNew && (
        <NewLeadDialog
          open={showNew}
          onOpenChange={setShowNew}
          onCreated={reload}
        />
      )}

      {pendingPerdido && (
        <MotivoPerdidaDialog
          leadId={pendingPerdido.leadId}
          onCancel={() => setPendingPerdido(null)}
          onSaved={() => { setPendingPerdido(null); reload(); }}
        />
      )}
    </div>
  );
};

// ---------------- Métricas ----------------

const MetricasView = ({ leads, loading }) => {
  const stats = useMemo(() => {
    const abiertos = leads.filter(isAbierto);
    const ganados = leads.filter(l => l.resultado === 'ganado').length;
    const perdidos = leads.filter(l => l.resultado === 'perdido').length;
    const hoy = abiertos.filter(isToday).length;
    const vencidos = abiertos.filter(isOverdue).length;

    const byKey = (key) => {
      const m = new Map();
      for (const l of leads) {
        const k = l[key] || '— Sin dato';
        m.set(k, (m.get(k) || 0) + 1);
      }
      return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
    };
    const byEtapa = ESTADOS.map(e => ({ key: e.label, count: abiertos.filter(l => l.estado === e.id).length }));

    return {
      total: leads.length,
      abiertos: abiertos.length,
      hoy, vencidos, ganados, perdidos,
      byEtapa,
      byProyecto: byKey('proyecto_edificio'),
      byOrigen: byKey('origen'),
      byTipologia: byKey('tipologia'),
    };
  }, [leads]);

  if (loading) return <div className="text-center text-slate-500 py-12">Cargando…</div>;

  return (
    <div className="space-y-4" data-testid="leads-metricas-view">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        <Kpi label="Total"     value={stats.total}     icon={Target} color="text-slate-700" />
        <Kpi label="Abiertos"  value={stats.abiertos}  icon={Target} color="text-slate-700" />
        <Kpi label="Hoy"       value={stats.hoy}       icon={Clock} color="text-blue-600" />
        <Kpi label="Vencidos"  value={stats.vencidos}  icon={AlertTriangle} color="text-rose-600" />
        <Kpi label="Ganados"   value={stats.ganados}   icon={Trophy} color="text-emerald-600" />
        <Kpi label="Perdidos"  value={stats.perdidos}  icon={XCircle} color="text-slate-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <BreakdownCard title="Leads abiertos por etapa" rows={stats.byEtapa.map(r => [r.key, r.count])} testid="metrics-by-etapa" />
        <BreakdownCard title="Leads por proyecto" rows={stats.byProyecto} testid="metrics-by-proyecto" />
        <BreakdownCard title="Leads por origen" rows={stats.byOrigen} testid="metrics-by-origen" />
        <BreakdownCard title="Leads por tipología" rows={stats.byTipologia} testid="metrics-by-tipologia" />
      </div>
    </div>
  );
};

const Kpi = ({ label, value, icon: Icon, color }) => (
  <Card className="p-3">
    <div className="flex items-center gap-2.5">
      <Icon className={`w-5 h-5 ${color}`} />
      <div>
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-xl font-semibold text-slate-900">{value}</div>
      </div>
    </div>
  </Card>
);

const BreakdownCard = ({ title, rows, testid }) => {
  const total = rows.reduce((s, r) => s + (r[1] || 0), 0) || 1;
  return (
    <Card className="p-4" data-testid={testid}>
      <h3 className="text-sm font-semibold text-slate-800 mb-3">{title}</h3>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 text-center py-2">Sin datos</div>
      ) : (
        <div className="space-y-1.5">
          {rows.map(([k, v]) => (
            <div key={k} className="flex items-center gap-2">
              <div className="text-xs text-slate-600 flex-1 truncate">{k}</div>
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-slate-700 rounded-full" style={{ width: `${(v / total) * 100}%` }} />
              </div>
              <div className="text-xs font-medium text-slate-700 w-8 text-right">{v}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

// ---------------- Filtros ----------------

const FilterSelect = ({ value, onChange, options, testid, shortLabel }) => {
  const isAll = value === 'todos';
  const current = options.find(o => o.value === value);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-[130px]" data-testid={testid}>
        <SelectValue>
          <span className={isAll ? 'text-slate-500' : 'text-slate-900'}>
            {isAll ? shortLabel : (current?.label || shortLabel)}
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map(opt => (
          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

// ---------------- Kanban ----------------

const KanbanColumn = ({ col, leads, isDragOver, onDragOver, onDragLeave, onDrop, onCardClick, onCardDragStart, onCardDragEnd }) => (
  <div
    onDragOver={onDragOver}
    onDragLeave={onDragLeave}
    onDrop={onDrop}
    className={`rounded-lg border bg-slate-50/60 flex flex-col transition ${
      isDragOver ? 'border-emerald-400 bg-emerald-50/70' : 'border-slate-200'
    }`}
    data-testid={`leads-column-${col.id}`}
  >
    <div className={`px-2.5 py-1.5 border-b border-slate-200 flex items-center justify-between rounded-t-lg ${col.color}`}>
      <span className="text-xs font-semibold truncate">{col.label}</span>
      <span className="text-[10px] font-medium bg-white/70 px-1.5 py-0.5 rounded">{leads.length}</span>
    </div>
    <div className="p-1.5 space-y-1.5 flex-1 overflow-y-auto min-h-[120px]" style={{ maxHeight: 'calc(100vh - 280px)' }}>
      {leads.length === 0 ? (
        <div className="text-center text-[11px] text-slate-400 py-4">—</div>
      ) : (
        leads.map(lead => (
          <LeadCard
            key={lead.lead_id}
            lead={lead}
            onClick={() => onCardClick(lead.lead_id)}
            onDragStart={() => onCardDragStart(lead.lead_id)}
            onDragEnd={onCardDragEnd}
          />
        ))
      )}
    </div>
  </div>
);

const LeadCard = ({ lead, onClick, onDragStart, onDragEnd }) => {
  const vencido = isOverdue(lead);
  const hoy = isToday(lead);
  const pri = PRIORIDADES.find(p => p.id === lead.prioridad) || PRIORIDADES[1];
  const dias = diasEnEtapa(lead);
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className="bg-white border border-slate-200 rounded-md p-2 cursor-pointer hover:border-amber-400 hover:shadow-sm transition active:opacity-50"
      data-testid={`lead-card-${lead.lead_id}`}
    >
      <div className="flex items-start justify-between gap-1 mb-1">
        <div className="text-[13px] font-semibold text-slate-900 truncate leading-tight">
          {lead.nombre} {lead.apellido}
        </div>
        <span className={`px-1 py-0 rounded text-[9px] font-bold ${pri.color}`}>{pri.label[0]}</span>
      </div>
      <div className="text-[11px] text-slate-600 truncate">
        {[lead.tipologia, lead.proyecto_edificio].filter(Boolean).join(' · ') || <span className="text-slate-400">Sin datos</span>}
      </div>
      {lead.whatsapp && (
        <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5 truncate">
          <MessageCircle className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{lead.whatsapp}</span>
        </div>
      )}
      <div className="flex items-center justify-between text-[11px] mt-1">
        <span className={`flex items-center gap-1 truncate ${vencido ? 'text-rose-600 font-medium' : hoy ? 'text-blue-600 font-medium' : 'text-slate-500'}`}>
          <CalendarIcon className="w-3 h-3 flex-shrink-0" />
          {lead.proximo_contacto ? formatProximo(lead.proximo_contacto) : <span className="text-slate-400">Sin fecha</span>}
        </span>
        <span className="text-slate-400 flex-shrink-0">{dias}d</span>
      </div>
      {vencido && (
        <div className="mt-1 text-[10px] px-1.5 py-0.5 bg-rose-50 text-rose-700 rounded inline-flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> Vencido
        </div>
      )}
    </div>
  );
};

// ---------------- Nuevo lead ----------------

const NewLeadDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState({
    nombre: '', apellido: '', whatsapp: '', email: '', empresa: '',
    tipo_operacion: '', uso_buscado: '', tipologia: '', proyecto_edificio: '',
    origen: '', prioridad: 'media',
    proximo_contacto: '', notas: '',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (k, v) => setForm(s => {
    const next = { ...s, [k]: v };
    // Si cambia a Alquiler, limpiar uso_buscado
    if (k === 'tipo_operacion' && v !== 'Compra') next.uso_buscado = '';
    return next;
  });

  const handleSubmit = async () => {
    if (!form.nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    try {
      setSaving(true);
      await leadsApi.create(form);
      toast.success('Lead creado');
      onCreated();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear el lead');
    } finally {
      setSaving(false);
    }
  };

  const showUso = form.tipo_operacion === 'Compra';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="leads-new-dialog">
        <DialogHeader>
          <DialogTitle>Nuevo lead</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <Section title="Datos básicos">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nombre *">
                <Input value={form.nombre} onChange={(e) => handleChange('nombre', e.target.value)} data-testid="leads-new-nombre" />
              </Field>
              <Field label="Apellido">
                <Input value={form.apellido} onChange={(e) => handleChange('apellido', e.target.value)} data-testid="leads-new-apellido" />
              </Field>
              <Field label="WhatsApp">
                <Input value={form.whatsapp} onChange={(e) => handleChange('whatsapp', e.target.value)} placeholder="+54 9 261..." data-testid="leads-new-whatsapp" />
              </Field>
              <Field label="Email">
                <Input value={form.email} onChange={(e) => handleChange('email', e.target.value)} data-testid="leads-new-email" />
              </Field>
              <Field label="Empresa">
                <Input value={form.empresa} onChange={(e) => handleChange('empresa', e.target.value)} data-testid="leads-new-empresa" />
              </Field>
              <Field label="Prioridad">
                <SimpleSelect value={form.prioridad} onChange={(v) => handleChange('prioridad', v)}
                  options={PRIORIDADES.map(p => ({ value: p.id, label: p.label }))} testid="leads-new-prioridad" />
              </Field>
            </div>
          </Section>

          <Section title="Datos comerciales">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tipo de operación">
                <SimpleSelect value={form.tipo_operacion} onChange={(v) => handleChange('tipo_operacion', v)} options={TIPOS_OP} testid="leads-new-tipoop" />
              </Field>
              <Field label="Uso buscado" hint={!showUso ? 'Solo aplica para Compra' : null}>
                <SimpleSelect value={form.uso_buscado} onChange={(v) => handleChange('uso_buscado', v)} options={USOS_BUSCADOS} testid="leads-new-uso" disabled={!showUso} />
              </Field>
              <Field label="Tipología">
                <SimpleSelect value={form.tipologia} onChange={(v) => handleChange('tipologia', v)} options={TIPOLOGIAS} testid="leads-new-tipologia" />
              </Field>
              <Field label="Proyecto">
                <SimpleSelect value={form.proyecto_edificio} onChange={(v) => handleChange('proyecto_edificio', v)} options={PROYECTOS} testid="leads-new-proyecto" />
              </Field>
              <Field label="Origen">
                <SimpleSelect value={form.origen} onChange={(v) => handleChange('origen', v)} options={ORIGENES} testid="leads-new-origen" />
              </Field>
              <Field label="Próximo contacto">
                <Input type="datetime-local" value={form.proximo_contacto} onChange={(e) => handleChange('proximo_contacto', e.target.value)} data-testid="leads-new-proximo" />
              </Field>
              <div className="col-span-2">
                <Field label="Notas">
                  <Textarea value={form.notas} onChange={(e) => handleChange('notas', e.target.value)} rows={3} data-testid="leads-new-notas" />
                </Field>
              </div>
            </div>
          </Section>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="leads-new-submit">
            {saving ? 'Guardando…' : 'Crear lead'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Drawer detalle ----------------

const LeadDrawer = ({ leadId, open, onOpenChange, onChanged, onMarkGanado, onMarkPerdido, onReabrir }) => {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [activityText, setActivityText] = useState('');
  const [activityTipo, setActivityTipo] = useState('nota');
  const [activityProximo, setActivityProximo] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey(k => k + 1);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await leadsApi.get(leadId);
        if (!alive) return;
        setLead(res.data);
        setForm(res.data);
      } catch (_) {
        if (alive) toast.error('No se pudo cargar el lead');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [leadId, reloadKey]);

  const saveEdit = async () => {
    try {
      await leadsApi.update(leadId, {
        nombre: form.nombre, apellido: form.apellido, email: form.email, whatsapp: form.whatsapp,
        empresa: form.empresa, cargo: form.cargo,
        tipo_operacion: form.tipo_operacion, uso_buscado: form.uso_buscado,
        tipologia: form.tipologia, proyecto_edificio: form.proyecto_edificio,
        unidad_interes: form.unidad_interes, presupuesto_estimado: form.presupuesto_estimado,
        origen: form.origen, prioridad: form.prioridad,
        proximo_contacto: form.proximo_contacto, notas: form.notas,
      });
      setEditing(false);
      reload();
      onChanged();
    } catch (e) {
      toast.error('No se pudo actualizar');
    }
  };

  const addActivity = async () => {
    const hasNota = activityText.trim();
    const hasProximo = activityProximo;
    if (!hasNota && activityTipo === 'nota' && !hasProximo) return;
    try {
      if (hasNota || activityTipo !== 'nota') {
        await leadsApi.addActivity(leadId, { tipo: activityTipo, nota: activityText });
      }
      if (hasProximo) {
        await leadsApi.update(leadId, { proximo_contacto: activityProximo });
      }
      setActivityText('');
      setActivityProximo('');
      reload();
      onChanged();
    } catch (e) {
      toast.error('No se pudo agregar la actividad');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar este lead? Esta acción no se puede deshacer.')) return;
    try {
      await leadsApi.remove(leadId);
      toast.success('Lead eliminado');
      onOpenChange(false);
      onChanged();
    } catch (e) {
      toast.error('No se pudo eliminar');
    }
  };

  const resultado = lead?.resultado || 'abierto';
  const cerrado = resultado !== 'abierto';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="leads-drawer">
        <SheetHeader>
          <SheetTitle>{lead ? `${lead.nombre || ''} ${lead.apellido || ''}`.trim() || 'Lead' : 'Lead'}</SheetTitle>
        </SheetHeader>

        {loading || !lead ? (
          <div className="text-center text-slate-500 py-12">Cargando…</div>
        ) : (
          <div className="mt-4 space-y-4">
            {/* Badges (sin etapa, solo cierre/alerta) */}
            {(cerrado || (!cerrado && (isOverdue(lead) || isToday(lead)))) && (
              <div className="flex items-center gap-2 flex-wrap">
                {resultado === 'ganado' && (
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
                    <Trophy className="w-3 h-3 mr-1" /> Ganado
                  </Badge>
                )}
                {resultado === 'perdido' && (
                  <Badge className="bg-rose-100 text-rose-700 border-rose-300">
                    <XCircle className="w-3 h-3 mr-1" /> Perdido
                  </Badge>
                )}
                {!cerrado && isOverdue(lead) && (
                  <Badge className="bg-rose-100 text-rose-700 border-rose-300">
                    <AlertTriangle className="w-3 h-3 mr-1" /> Vencido
                  </Badge>
                )}
                {!cerrado && isToday(lead) && (
                  <Badge className="bg-blue-100 text-blue-700 border-blue-300">
                    <Clock className="w-3 h-3 mr-1" /> Hoy
                  </Badge>
                )}
              </div>
            )}

            {/* Acciones de cierre */}
            <Card className="p-3">
              {!cerrado ? (
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => onMarkGanado(leadId)} className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1" data-testid="leads-drawer-ganado-btn">
                    <Trophy className="w-3.5 h-3.5 mr-1" /> Marcar como Ganado
                  </Button>
                  <Button size="sm" onClick={() => onMarkPerdido(leadId)} variant="outline" className="text-rose-600 border-rose-300 hover:bg-rose-50 flex-1" data-testid="leads-drawer-perdido-btn">
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Marcar como Perdido
                  </Button>
                </div>
              ) : (
                <Button size="sm" variant="outline" onClick={() => onReabrir(leadId)} data-testid="leads-drawer-reabrir-btn">
                  Reabrir lead
                </Button>
              )}
            </Card>

            {/* Actividad (antes de Datos) */}
            <ActivitySection
              lead={lead}
              activityText={activityText}
              setActivityText={setActivityText}
              activityTipo={activityTipo}
              setActivityTipo={setActivityTipo}
              onAdd={addActivity}
              activityProximo={activityProximo}
              setActivityProximo={setActivityProximo}
            />

            {!editing ? (
              <Card className="p-3 space-y-1.5 text-sm">
                <Row icon={MessageCircle} label="WhatsApp">{lead.whatsapp || '—'}</Row>
                <Row icon={Mail} label="Email">{lead.email || '—'}</Row>
                <Row icon={Building2} label="Proyecto">{[lead.proyecto_edificio, lead.unidad_interes].filter(Boolean).join(' · ') || '—'}</Row>
                <Row icon={Target} label="Operación / Tipología">{[lead.tipo_operacion, lead.tipologia, lead.uso_buscado].filter(Boolean).join(' · ') || '—'}</Row>
                <Row icon={MapPin} label="Origen">{lead.origen || '—'}</Row>
                <Row icon={CalendarIcon} label="Próximo contacto">{lead.proximo_contacto ? formatProximo(lead.proximo_contacto) : '—'}</Row>
                {lead.empresa && <Row icon={Building2} label="Empresa">{lead.empresa}{lead.cargo ? ` · ${lead.cargo}` : ''}</Row>}
                {lead.presupuesto_estimado && <Row icon={FileText} label="Presupuesto">{lead.presupuesto_estimado}</Row>}
                {lead.notas && (
                  <div className="pt-2 mt-2 border-t border-slate-100">
                    <Label className="text-xs text-slate-500">Notas</Label>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{lead.notas}</p>
                  </div>
                )}
                {resultado === 'perdido' && lead.motivo_perdida && (
                  <div className="pt-2 mt-2 border-t border-rose-100 bg-rose-50 -mx-3 -mb-3 px-3 py-2 rounded-b">
                    <Label className="text-xs text-rose-700">Motivo de pérdida</Label>
                    <p className="text-sm text-rose-800">{lead.motivo_perdida}</p>
                    {lead.comentario_perdida && <p className="text-xs text-rose-600 mt-1">{lead.comentario_perdida}</p>}
                  </div>
                )}
                <div className="pt-2 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="leads-drawer-edit-btn">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Editar datos
                  </Button>
                  <Button size="sm" variant="outline" className="text-rose-600 hover:bg-rose-50" onClick={handleDelete} data-testid="leads-drawer-delete-btn">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                  </Button>
                </div>
              </Card>
            ) : (
              <Card className="p-3 grid grid-cols-2 gap-2">
                <Field label="Nombre">
                  <Input value={form.nombre || ''} onChange={(e) => setForm(s => ({ ...s, nombre: e.target.value }))} />
                </Field>
                <Field label="Apellido">
                  <Input value={form.apellido || ''} onChange={(e) => setForm(s => ({ ...s, apellido: e.target.value }))} />
                </Field>
                <Field label="WhatsApp">
                  <Input value={form.whatsapp || ''} onChange={(e) => setForm(s => ({ ...s, whatsapp: e.target.value }))} />
                </Field>
                <Field label="Email">
                  <Input value={form.email || ''} onChange={(e) => setForm(s => ({ ...s, email: e.target.value }))} />
                </Field>
                <Field label="Empresa">
                  <Input value={form.empresa || ''} onChange={(e) => setForm(s => ({ ...s, empresa: e.target.value }))} />
                </Field>
                <Field label="Cargo">
                  <Input value={form.cargo || ''} onChange={(e) => setForm(s => ({ ...s, cargo: e.target.value }))} />
                </Field>
                <Field label="Tipo operación">
                  <SimpleSelect value={form.tipo_operacion || ''} onChange={(v) => setForm(s => ({ ...s, tipo_operacion: v, uso_buscado: v === 'Compra' ? s.uso_buscado : '' }))} options={TIPOS_OP} />
                </Field>
                <Field label="Uso buscado" hint={form.tipo_operacion !== 'Compra' ? 'Solo para Compra' : null}>
                  <SimpleSelect value={form.uso_buscado || ''} onChange={(v) => setForm(s => ({ ...s, uso_buscado: v }))} options={USOS_BUSCADOS} disabled={form.tipo_operacion !== 'Compra'} />
                </Field>
                <Field label="Tipología">
                  <SimpleSelect value={form.tipologia || ''} onChange={(v) => setForm(s => ({ ...s, tipologia: v }))} options={TIPOLOGIAS} />
                </Field>
                <Field label="Proyecto">
                  <SimpleSelect value={form.proyecto_edificio || ''} onChange={(v) => setForm(s => ({ ...s, proyecto_edificio: v }))} options={PROYECTOS} />
                </Field>
                <Field label="Origen">
                  <SimpleSelect value={form.origen || ''} onChange={(v) => setForm(s => ({ ...s, origen: v }))} options={ORIGENES} />
                </Field>
                <Field label="Prioridad">
                  <SimpleSelect value={form.prioridad || 'media'} onChange={(v) => setForm(s => ({ ...s, prioridad: v }))} options={PRIORIDADES.map(p => ({ value: p.id, label: p.label }))} />
                </Field>
                <Field label="Próximo contacto">
                  <Input type="datetime-local" value={form.proximo_contacto ? toLocalInput(form.proximo_contacto) : ''} onChange={(e) => setForm(s => ({ ...s, proximo_contacto: e.target.value }))} />
                </Field>
                <div />
                <div className="col-span-2">
                  <Field label="Notas">
                    <Textarea value={form.notas || ''} onChange={(e) => setForm(s => ({ ...s, notas: e.target.value }))} rows={3} />
                  </Field>
                </div>
                <div className="col-span-2 flex justify-end gap-2 mt-1">
                  <Button size="sm" variant="outline" onClick={() => { setEditing(false); setForm(lead); }}>Cancelar</Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveEdit} data-testid="leads-drawer-save-btn">Guardar</Button>
                </div>
              </Card>
            )}

            {/* Actividad ya fue mostrada arriba */}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// ---------------- Sección Actividad ----------------

const ActivitySection = ({ lead, activityText, setActivityText, activityTipo, setActivityTipo, onAdd, activityProximo, setActivityProximo }) => (
  <Card className="p-3">
    <Label className="text-xs text-slate-500 mb-2 block">Agregar actividad</Label>
    <div className="flex gap-2 mb-2">
      <SimpleSelect
        value={activityTipo}
        onChange={setActivityTipo}
        options={[
          { value: 'nota', label: 'Nota' },
          { value: 'llamada', label: 'Llamada' },
          { value: 'whatsapp', label: 'WhatsApp' },
          { value: 'email', label: 'Email' },
          { value: 'visita', label: 'Visita' },
          { value: 'propuesta', label: 'Propuesta' },
        ]}
        className="w-32"
      />
      <Input
        value={activityText}
        onChange={(e) => setActivityText(e.target.value)}
        placeholder="Detalle (opcional)…"
        data-testid="leads-drawer-activity-input"
      />
      <Button size="sm" onClick={onAdd} data-testid="leads-drawer-activity-add">
        <Plus className="w-4 h-4" />
      </Button>
    </div>
    <div className="flex items-center gap-2 mb-3">
      <Label className="text-xs text-slate-500 w-32 flex-shrink-0">Próximo contacto</Label>
      <Input
        type="datetime-local"
        value={activityProximo}
        onChange={(e) => setActivityProximo(e.target.value)}
        className="h-8 text-xs"
        data-testid="leads-drawer-activity-proximo"
      />
    </div>
    <div className="space-y-2 max-h-64 overflow-y-auto border-t border-slate-100 pt-2">
      {(lead.actividades || []).length === 0 ? (
        <div className="text-xs text-slate-400 text-center py-3">Sin actividades</div>
      ) : (
        (lead.actividades || []).map(a => (
          <div key={a.activity_id} className="text-xs border-l-2 border-slate-200 pl-2 py-0.5">
            <div className="flex items-center justify-between">
              <span className="font-medium text-slate-700 capitalize">{a.tipo.replace('_', ' ')}</span>
              <span className="text-slate-400">{new Date(a.fecha).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
            </div>
            {a.nota && <div className="text-slate-600 mt-0.5">{a.nota}</div>}
          </div>
        ))
      )}
    </div>
    {lead.proximo_contacto && (
      <div className="mt-2 text-xs text-slate-500 pt-2 border-t border-slate-100">
        Próximo contacto actual: <strong className="text-slate-700">{formatProximo(lead.proximo_contacto)}</strong>
      </div>
    )}
  </Card>
);

// ---------------- Motivo de pérdida ----------------

const MotivoPerdidaDialog = ({ leadId, onCancel, onSaved }) => {
  const [motivo, setMotivo] = useState('');
  const [motivoOtro, setMotivoOtro] = useState('');
  const [comentario, setComentario] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const final = motivo === 'Otro' ? motivoOtro.trim() : motivo;
    if (!final) { toast.error('Indicá un motivo'); return; }
    try {
      setSaving(true);
      await leadsApi.changeResultado(leadId, {
        resultado: 'perdido',
        motivo_perdida: final,
        comentario_perdida: comentario.trim() || null,
      });
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo marcar como perdido');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!leadId} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md" data-testid="leads-motivo-dialog">
        <DialogHeader>
          <DialogTitle>Marcar como Perdido</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Field label="Motivo *">
            <SimpleSelect value={motivo} onChange={setMotivo} options={MOTIVOS_PERDIDA} testid="leads-motivo-select" />
          </Field>
          {motivo === 'Otro' && (
            <Field label="Detallar motivo">
              <Input value={motivoOtro} onChange={(e) => setMotivoOtro(e.target.value)} data-testid="leads-motivo-otro" />
            </Field>
          )}
          <Field label="Comentario adicional">
            <Textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows={3} data-testid="leads-motivo-comentario" />
          </Field>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onCancel}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="leads-motivo-submit">
            {saving ? 'Guardando…' : 'Marcar como Perdido'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Helpers visuales ----------------

const Field = ({ label, hint, children }) => (
  <div>
    <Label className="text-xs text-slate-500 mb-1 block">
      {label}{hint && <span className="text-slate-400 font-normal italic ml-1">— {hint}</span>}
    </Label>
    {children}
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">{title}</div>
    {children}
  </div>
);

const Row = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-2 text-sm">
    <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="text-[10px] text-slate-400 uppercase">{label}</div>
      <div className="text-slate-800 truncate">{children}</div>
    </div>
  </div>
);

const SimpleSelect = ({ value, onChange, options, testid, className, disabled }) => {
  const opts = options.map(o => typeof o === 'string' ? { value: o, label: o } : o);
  return (
    <Select value={value || ''} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className={`h-9 ${className || ''}`} data-testid={testid}>
        <SelectValue placeholder="Seleccionar…" />
      </SelectTrigger>
      <SelectContent>
        {opts.map(o => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default ComercialLeads;
